import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";

import { useRootStore } from "store";
import {
  clearWorkingTabCustomer,
  clearCompanyName,
  logoutUser
} from "common/MetaActions";
import logo from "assets/images/LUDVIG_CO_LOGO_RGB_WHITE.svg";
import config from "config/config";
import { useGlobalContext } from "app/store/GlobalContext";
import { useSecondaryMenuItems } from "hooks/use-secondary-menu-items";
import MainMenuFullscreen from "./components/mainMenu/MainMenuFullscreen";
import ExternalUserCustomerMenu from "./components/mainMenu/dropdownMenu/ExternalUserCustomerMenu";
import CustomerNavLinks from "./components/CustomerNavLinks";
import UserProfile from "./components/UserProfile";
import SecondaryMenu from "./components/SecondaryMenu";

import "./Header.scss";

export default function Header() {
  const { handleNavigation } = useGlobalContext();
  const navigate = useNavigate();
  const location = useLocation();

  const { user, global } = useRootStore((state) => state);
  const { myCustomers } = useRootStore((state) => state.customers);
  const { unfilteredCustomers: customers } = myCustomers;
  const { setUnreadThreadsStatus } = useRootStore((state) => state.status);

  const { isAuthenticated } = user;

  const { setWorkingCustomerId, resetCustomer } = useRootStore(
    (state) => state.global
  );

  const isInternalUser = user.userInfo && user.userInfo.type === "Internal";

  const [toggleFullScreenMenu, setToggleFullScreenMenu] = useState(false);

  // Use the shared hook for secondary menu items
  const secondaryMenuItems = useSecondaryMenuItems();

  // Update unread threads count when navigating between tabs
  useEffect(() => {
    const secondaryMenuPaths = [
      "/dashboard",
      "/inbox",
      "/archive",
      "/delivery",
      "/inventory",
      "/documents"
    ];

    const isSecondaryMenuPath = secondaryMenuPaths.some((path) =>
      location.pathname.startsWith(path)
    );

    if (isSecondaryMenuPath && global.workingCustomerId) {
      setUnreadThreadsStatus(global.workingCustomerId);
    }
  }, [location.pathname, global.workingCustomerId, setUnreadThreadsStatus]);

  const handleHomeClick = (
    e: React.MouseEvent<HTMLImageElement, MouseEvent>,
    onCloseMenu?: () => void
  ) => {
    e.preventDefault();

    const clickEvent = () => {
      if (onCloseMenu) {
        onCloseMenu();
      }
    };

    if (isAuthenticated) {
      if (isInternalUser) {
        handleNavigation({
          url: "/customers",
          eventTitle: "customers",
          e: e,
          clickEvent
        });
      } else {
        handleNavigation({
          url: "/dashboard",
          eventTitle: "dashboard",
          e: e,
          clickEvent
        });
      }
    } else {
      handleNavigation({ url: "/", eventTitle: "root", e: e, clickEvent });
    }
  };

  const handleLogout = () => {
    logoutUser();
    window.location.href = "/";
  };

  const handleChangeCustomerNavigation = (customerId: string) => {
    if (customerId !== global.workingCustomerId) {
      handleNavigation({
        url: "/dashboard",
        clickEvent: () => handleChangeCustomer(customerId)
      });
    }
  };

  const handleChangeCustomer = async (customerId: string) => {
    clearWorkingTabCustomer();
    clearCompanyName();
    resetCustomer();
    setWorkingCustomerId(customerId);
    navigate(`/dashboard`);
  };

  const renderLogo = () => {
    let logoText = "MittKontor";
    if (config.stage !== "production") {
      logoText += ` (${config.stage?.toUpperCase()})`;
    }

    return (
      <div className="logoHeader">
        <img
          alt="Ludvig &amp; Co"
          src={logo}
          onClick={(e) => {
            handleHomeClick(e, () => setToggleFullScreenMenu(false));
          }}
          style={{ cursor: "pointer" }}
        />
        <div className="logoText">{logoText}</div>
      </div>
    );
  };

  const showSecondaryMenu = () => {
    // Don't show on these paths
    const excludedPaths = [
      "/addCustomer",
      "/wizard",
      "/admin",
      "/settings",
      "/customers",
      "/customers/all",
      "/customers/my"
    ];

    return !excludedPaths.includes(location.pathname);
  };

  const isLoggedIn = user.isAuthenticated;
  const shouldShowSecondaryMenu = showSecondaryMenu();

  return (
    <header className="header no-print">
      <div className="headerPrimaryRow">
        <div className="headerContent">
          <div className="headerLeft">
            {renderLogo()}
            {isLoggedIn && (
              <>
                {isInternalUser ? (
                  <>
                    <div className="headerDivider">|</div>
                    <CustomerNavLinks
                      handleChangeCustomerNavigation={
                        handleChangeCustomerNavigation
                      }
                    />
                  </>
                ) : (
                  customers.length > 1 && (
                    <div className="externalUserCustomerSection">
                      <div className="headerDivider">|</div>
                      <ExternalUserCustomerMenu
                        handleChangeCustomer={handleChangeCustomerNavigation}
                      />
                    </div>
                  )
                )}
              </>
            )}
          </div>

          {isLoggedIn && (
            <>
              <div className="headerRight">
                <UserProfile onLogout={handleLogout} />
              </div>
              <MainMenuFullscreen
                secondaryMenuItems={
                  shouldShowSecondaryMenu ? secondaryMenuItems : []
                }
                toggleFullScreenMenu={toggleFullScreenMenu}
                setToggleFullScreenMenu={setToggleFullScreenMenu}
                onLogout={handleLogout}
                userInfo={user.userInfo}
                customers={customers}
                handleChangeCustomer={handleChangeCustomerNavigation}
              />
            </>
          )}
        </div>
      </div>

      {isLoggedIn && shouldShowSecondaryMenu && (
        <div className="headerSecondaryRow">
          <SecondaryMenu menuItems={secondaryMenuItems} />
        </div>
      )}
    </header>
  );
}
